import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config';
import { Company } from '../../types';
import { generateClientKey } from './keys';
import { createExpensesLedger, getCompanyLedger } from './expensesLedgers';

// Companies Collection
export const addCompany = async (company: Omit<Company, 'id' | 'createdAt'>): Promise<string> => {
  try {
    // Create custom key: name-uniqueId
    const customKey = generateClientKey(company.name, company.uniqueId);
    
    // Check if company already exists
    const existingCompany = await getCompanyById(customKey);
    if (existingCompany) {
      throw new Error(`Company with name ${company.name} and uniqueId ${company.uniqueId} already exists`);
    }
    
    const docRef = doc(db, 'companies', customKey);
    await setDoc(docRef, {
      ...company,
      createdAt: serverTimestamp()
    });
    
    // Automatically create expenses ledger for the company with opening balance
    await createExpensesLedger(customKey, company.name, company.openingBalance || 0);
    
    return customKey;
  } catch (error) {
    throw new Error(`Error adding company: ${error}`);
  }
};

export const getCompanies = async (): Promise<Company[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'companies'));
    const companies = querySnapshot.docs.map(doc => ({
      id: doc.id, // This will now be the custom key (name-uniqueId)
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    })) as Company[];

    // Calculate outstanding using computed ledger (includes opening balance and payments)
    const withOutstanding = await Promise.all(
      companies.map(async (company) => {
        try {
          const ledger = await getCompanyLedger(company.id);
          return { ...company, outstanding: ledger.currentBalance } as Company;
        } catch (error) {
          return company;
        }
      })
    );

    return withOutstanding;
  } catch (error) {
    throw new Error(`Error getting companies: ${error}`);
  }
};

export const getCompanyById = async (companyId: string): Promise<Company | null> => {
  try {
    const docRef = doc(db, 'companies', companyId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id, // This will be the custom key
        ...data,
        createdAt: data.createdAt?.toDate() || new Date()
      } as Company;
    }
    return null;
  } catch (error) {
    throw new Error(`Error getting company: ${error}`);
  }
};

// Helper function to get company by name and uniqueId
export const getCompanyByNameAndUniqueId = async (name: string, uniqueId: string): Promise<Company | null> => {
  try {
    const customKey = generateClientKey(name, uniqueId);
    return await getCompanyById(customKey);
  } catch (error) {
    throw new Error(`Error getting company by name and uniqueId: ${error}`);
  }
};

// Delete company and all related data
export const deleteCompany = async (companyId: string): Promise<void> => {
  try {
    // Delete the company document
    const companyRef = doc(db, 'companies', companyId);
    await deleteDoc(companyRef);
    
    // Delete all related expenses invoices
    const invoicesQuery = query(
      collection(db, 'expensesInvoices'),
      where('clientId', '==', companyId)
    );
    const invoicesSnapshot = await getDocs(invoicesQuery);
    const deleteInvoicePromises = invoicesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteInvoicePromises);
    
    // Delete all related ledger entries
    const ledgerQuery = query(
      collection(db, 'ledgerEntries'),
      where('clientId', '==', companyId)
    );
    const ledgerSnapshot = await getDocs(ledgerQuery);
    const deleteLedgerPromises = ledgerSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteLedgerPromises);
    
    // Delete the expenses ledger
    const expensesLedgerQuery = query(
      collection(db, 'expensesLedgers'),
      where('companyId', '==', companyId)
    );
    const expensesLedgerSnapshot = await getDocs(expensesLedgerQuery);
    const deleteExpensesLedgerPromises = expensesLedgerSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteExpensesLedgerPromises);
  } catch (error) {
    throw new Error(`Error deleting company: ${error}`);
  }
};
